import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PrintIcon from '@mui/icons-material/Print';
import { SaleTender } from '../../api';
import { formatCents } from '../money';
import { COLORS } from '../theme';

/** Auto-dismiss so a distracted cashier doesn't leave the summary up
 *  and start the next sale into a dead screen. */
const AUTO_DISMISS_MS = 6000;

export type CompletedSale = {
  /** The recorded sale's id, so the summary can ask for a receipt after
   *  the fact. Absent when the write failed — there is nothing to
   *  reprint, and the button hides itself. */
  id?: string;
  total: number;
  /** The sale's own tender vocabulary, not a narrowed copy of it —
   *  a hand-maintained subset drifts the moment a new tender appears,
   *  and `none` (owner withdrawal) did exactly that. */
  tender: SaleTender;
  /** Cash only. */
  tenderedCents?: number;
  changeCents?: number;
  wantsReceipt: boolean;
  /** Whether a printer actually took the receipt. Only meaningful when
   *  `wantsReceipt` — a cashier who asked for paper needs to know when
   *  none is coming. */
  receiptPrinted: boolean;
  employee: boolean;
  /** Owner withdrawal — stock left the shelf, no money changed hands. */
  owner: boolean;
  /** False when the sale could not be written to the server. The money
   *  has already changed hands by this point, so the summary still
   *  shows — with a warning, because someone has to fix it. */
  recorded: boolean;
  /** True when the server is in test mode and this sale was recorded as
   *  test data. */
  isTest: boolean;
};

type Props = {
  sale: CompletedSale | null;
  onDismiss: () => void;
  /** Print a receipt for the sale just completed. The single most
   *  common receipt request is "actually, can I have one?" a few
   *  seconds after saying no, and this dialog is where that lands. */
  onPrintReceipt: () => void;
};

const SaleCompleteDialog: React.FC<Props> = ({
  sale,
  onDismiss,
  onPrintReceipt,
}) => {
  const [printing, setPrinting] = useState(false);
  const [printed, setPrinted] = useState(false);

  useEffect(() => {
    // A new sale resets the button, or the second customer in a row
    // would see the first one's "Sent to printer".
    setPrinting(false);
    setPrinted(false);
  }, [sale?.id]);

  useEffect(() => {
    if (!sale) return;
    // Asking for a receipt is a deliberate act, and losing the dialog
    // out from under it would be maddening. The auto-dismiss and the
    // any-key dismiss both stand down once the button has been used.
    if (printing || printed) return;
    const t = setTimeout(onDismiss, AUTO_DISMISS_MS);
    // Any keypress also clears it — a scan of the next customer's first
    // item shouldn't be swallowed by a summary screen.
    const onKey = () => onDismiss();
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey);
    };
  }, [sale, onDismiss, printing, printed]);

  return (
    <Dialog open={sale !== null} onClose={onDismiss} maxWidth="sm" fullWidth>
      {sale && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 72, color: COLORS.accent }} />
          <Typography sx={{ fontWeight: 800, fontSize: '1.75rem', mt: 1, mb: 3 }}>
            {sale.owner ? 'WITHDRAWAL RECORDED' : 'SALE COMPLETE'}
          </Typography>

          <Box sx={{ maxWidth: 320, mx: 'auto', textAlign: 'left', mb: 3 }}>
            <Row label="Total" value={formatCents(sale.total)} />
            {sale.tender === 'cash' ? (
              <>
                <Row label="Cash" value={formatCents(sale.tenderedCents ?? 0)} />
                <Row
                  label="Change"
                  value={formatCents(sale.changeCents ?? 0)}
                  emphasise
                />
              </>
            ) : sale.tender === 'none' ? (
              <Row label="Paid" value="Nothing — owner withdrawal" />
            ) : (
              <Row
                label="Paid by"
                value={sale.tender === 'card' ? 'Card' : 'Other'}
              />
            )}
            <Row
              label="Receipt"
              value={
                !sale.wantsReceipt
                  ? 'No'
                  : sale.receiptPrinted
                    ? 'Printing'
                    : 'Not printed'
              }
            />
            {sale.employee && <Row label="Pricing" value="Employee (cost)" />}
            {sale.owner && <Row label="Pricing" value="Owner (cost, untaxed)" />}
          </Box>

          {/* A third warning, and a milder one than the two below: the
              sale is fine and the money is right, there just isn't any
              paper. Usually the counter's print service is down. */}
          {sale.wantsReceipt && !sale.receiptPrinted && (
            <Box
              sx={{
                backgroundColor: COLORS.warnBg,
                border: `2px solid ${COLORS.warn}`,
                color: COLORS.warnText,
                borderRadius: 2,
                px: 2,
                py: 1.5,
                mb: 3,
                fontWeight: 700,
              }}
            >
              NO RECEIPT PRINTED — the printer service didn't take it.
              The sale is recorded; it can be reprinted from the printer
              page.
            </Box>
          )}

          {/* Two different warnings, and they are not the same thing.
              A test sale is working as configured; an unrecorded sale is
              money taken with no record of it, and needs a person. */}
          {!sale.recorded && (
            <Box
              sx={{
                backgroundColor: COLORS.warnBg,
                border: `2px solid ${COLORS.warn}`,
                color: COLORS.warnText,
                borderRadius: 2,
                px: 2,
                py: 1.5,
                mb: 3,
                fontWeight: 700,
              }}
            >
              ⚠ NOT RECORDED — the sale did not reach the server. Tell a
              manager; the totals above are still correct.
            </Box>
          )}

          {sale.isTest && sale.recorded && (
            <Box
              sx={{
                backgroundColor: COLORS.warnBg,
                border: `2px solid ${COLORS.warn}`,
                color: COLORS.warnText,
                borderRadius: 2,
                px: 2,
                py: 1.5,
                mb: 3,
                fontWeight: 700,
              }}
            >
              ⚠ TEST MODE — recorded as test data, not a real sale
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            {sale.id && (
              <Button
                variant="outlined"
                disabled={printing || printed}
                onClick={() => {
                  setPrinting(true);
                  onPrintReceipt();
                  setPrinting(false);
                  setPrinted(true);
                }}
                sx={{
                  flex: 1,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  borderWidth: 2,
                }}
              >
                <PrintIcon sx={{ mr: 1 }} />
                {printed ? 'SENT TO PRINTER' : 'PRINT RECEIPT'}
              </Button>
            )}
            <Button
              variant="contained"
              onClick={onDismiss}
              sx={{ flex: 1, py: 2, fontSize: '1.2rem', fontWeight: 700 }}
            >
              NEW SALE
            </Button>
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

const Row: React.FC<{ label: string; value: string; emphasise?: boolean }> = ({
  label,
  value,
  emphasise,
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
    <Typography sx={{ color: COLORS.textMuted }}>{label}</Typography>
    <Typography
      sx={{
        fontWeight: emphasise ? 800 : 600,
        fontSize: emphasise ? '1.4rem' : '1.1rem',
        color: emphasise ? COLORS.accent : COLORS.text,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </Typography>
  </Box>
);

export default SaleCompleteDialog;
